/* ═══════════════════════════════════════════════════════════════════════════
   ManDana — the signed-out home page
   ---------------------------------------------------------------------------
   The front door. A pitch-black stage carrying the Droplet Film, and one
   control over it: SIGN IN, centred, 52px off the bottom, live from the first
   frame to the last. Nobody has to wait fifty-seven seconds to get in.

   COVER, NOT CONTAIN. The film is drawn on a fixed 1920×1080 stage and the
   viewport is whatever the visitor brought. In landscape the stage simply
   fills it. In portrait it is oversized until its HEIGHT covers the screen and
   the sides crop — a letterboxed film with black bars above and below would be
   a video player, and this is a page. The arithmetic is the design file's own,
   less the 44px it added to clear a playback bar that does not ship.

   REDUCED MOTION gets the final frame, held: the mark, the name, the line and
   the same live button. Not a stripped page — the ending. The whole statement
   the film builds toward is in that frame, so there is nothing to apologise for
   and nothing to miss.

   THE WAY IN IS ONE QUESTION. SIGN IN used to raise the device's passkey sheet,
   and three doors appeared behind a failure — create an account, add this
   device, recover an account. All three predate the invitation gate, and they
   cost an invited person a dead press and a dismissed system dialogue before
   anything on screen would talk to them.

   So SIGN IN asks the question instead: the name somebody else wrote down,
   First Name focused. And while that panel stands, the device quietly offers
   its own passkey — WebAuthn conditional mediation, armed here and disarmed
   inside the ceremonies themselves (app/md-sync.js). One passkey on the device
   and the operating system puts it in the suggestion bar over the keyboard;
   several and it shows its picker; none and the keyboard is just a keyboard.

   THE NAME STILL DECIDES EVERYTHING. Vouched for and unclaimed goes to the
   arrival; unknown goes to the waiting list; already taken up asks that
   account's passkey by name. Only when THAT fails is anybody told anything is
   wrong — and then by name, with three ways back in: try again, link this
   device, or ask the person who vouched for you. At rest the page is still
   exactly as drawn.
   ═══════════════════════════════════════════════════════════════════════════ */

const { useState: mdlUseState, useEffect: mdlUseEffect, useRef: mdlUseRef, useCallback: mdlUseCallback } = React;

const MD_LANDING_MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/* How often a screen waiting to be approved asks whether it has been. Three
   seconds is the gap between "instant" and a request every frame: the person is
   looking at two screens at once, so the delay they notice is the one after
   they press APPROVE on the other one. */
const MD_PAIR_POLL_MS = 3000;
/* How long the door waits for the browser to say what devices it has before it
   treats silence as an answer. See `camera` in MdLanding. */
const MD_CAMERA_ASK_MS = 1500;

/* Design tokens, straight from Landing Page.dc.html. `rest` is the drawn
   default; the design's own prop offered #A585DC and #C3A9F0 as alternatives
   and the hover state is the lightest of the three. */
const MD_SIGNIN = { rest: '#7B5BC0', restOpacity: 0.75, hover: '#C3A9F0', pearl: '#FAF9FC' };

/* The same words Settings uses for the same failures (app/md-settings.jsx).
   Two vocabularies for one refusal is how a product starts sounding like two. */
const MD_LANDING_ERRORS = {
  cancelled: null,                      // dismissing the system sheet is not an error
  unsupported: 'This device does not support passkeys.',
  invalid_code: 'That code was not recognised, or has already been used.',
  counter_regression: 'This passkey looks like it has been copied. Signing in was refused.',
  unknown_credential: 'No account found for that passkey on this server.',
  /* The invitation's own refusals. Each one has to be distinguishable from the
     others, because each sends the person somewhere different. */
  invite_required: 'A ManDana user has to write your name down before an account can be made.',
  not_verified: 'That name is not on the list.',
  already_claimed: 'That username has already been taken up. If it is yours, sign in with it instead.',
  unknown_username: 'ManDana does not know that username.',
  unreachable: 'The server could not be reached, so nothing was changed. Try again when you are back online.',
  /* Not "something went wrong": there is nothing wrong with the name they
     typed, and typing a different one will not help. Something between this
     device and ManDana answered instead of ManDana — see mdPostJson. */
  malformed: 'The server answered something this app could not read, so nothing was changed. Try again in a moment.',
};

/* Marwan's sentence, and it is the whole of what an uninvited visitor is ever
   told. Held as a constant so it cannot be paraphrased by the next person to
   touch this file. */
const MD_WAITING_LIST = 'Thank you for your interest in ManDana. You are now on our honorable waiting list.';
const mdLandingSay = (err) =>
  (err in MD_LANDING_ERRORS ? MD_LANDING_ERRORS[err] : 'Something went wrong. Please try again.');

/* Why the camera did not open — and every one of these ends in the code field,
   which was never taken away. `denied` is deliberately not here: the person
   refused it themselves, in their browser's own dialogue, and telling them what
   they just did is the app not having been listening. */
const MD_LANDING_SCAN_ERRORS = {
  no_camera: 'No camera on this device. Type the code instead, or show a code of your own.',
  in_use: 'The camera is being used by another app. Type the code instead.',
  unsupported: 'This browser cannot read a code. Type it instead.',
};
const mdLandingScanSay = (err) =>
  MD_LANDING_SCAN_ERRORS[err] || 'The camera would not open. Type the code instead.';

/** Screen-reader-only. The film says nothing until second 51; this says it at once. */
const MD_SR_ONLY = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

/* ── the stage ─────────────────────────────────────────────────────────────
   Holds the film at its authored size and scales it to cover. Separated from
   the page so the sign-in surface below never re-renders at 60fps alongside it. */
function MdFilmStage() {
  const { cues, total } = window.mdFilmCues(window.MD_FILM_SCENES);
  const [t, setT] = mdlUseState(0);

  const still = mdlUseRef(false);
  if (still.current === false && typeof window.matchMedia === 'function') {
    still.current = !!window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  const [box, setBox] = mdlUseState(() => mdLandingBox());

  mdlUseEffect(() => {
    const onResize = () => setBox(mdLandingBox());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  mdlUseEffect(() => window.mdFilmDrive({ total, still: still.current }, setT), [total]);

  const Film = window.MdDropletFilm;

  return (
    <div style={{ position: 'absolute', inset: 0, background: '#000', overflow: 'hidden' }} aria-hidden="true">
      {/* The stage stays at its authored 1920×1080 and is scaled as a whole, so
          every coordinate inside the film is the one it was drawn with. */}
      <div style={{
        position: 'absolute', left: box.left, top: box.top, width: 1920, height: 1080,
        transform: `scale(${box.scale})`, transformOrigin: 'top left',
      }}>
        <Film t={t} cues={cues} />
      </div>
    </div>
  );
}

/**
 * Cover arithmetic: where to put the 1920×1080 stage, and how big.
 *
 * The LARGER of the two ratios wins — that is the single line that makes this
 * cover rather than fit, and it is why a portrait phone gets a full-bleed film
 * with the sides cropped instead of a letterboxed one with black bars. The
 * result is then centred so the crop is even.
 *
 * A plain function of two numbers, so it can be checked without a browser.
 */
function mdLandingBox(vw, vh) {
  const w = vw || window.innerWidth || 1920;
  const h = vh || window.innerHeight || 1080;
  const scale = Math.max(w / 1920, h / 1080);
  return {
    scale,
    left: Math.round((w - 1920 * scale) / 2),
    top: Math.round((h - 1080 * scale) / 2),
  };
}

/**
 * Where a panel is allowed to stand, and how tall it may be.
 *
 * iOS raises the keyboard WITHOUT changing the layout viewport: `100%` still
 * means the whole page, so a panel centred in it is centred behind the keys. The
 * visual viewport is the only thing that knows a keyboard is up — `height` is
 * what is left, `offsetTop` is where it starts once the page is scrolled or
 * pinch-zoomed.
 *
 * A plain function of one object, so the arithmetic can be checked without a
 * phone. No viewport, or one reporting nothing, means the whole page — which is
 * every desktop browser and every test that never stubs one.
 */
function mdLandingKeyboardBox(vv) {
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const height = vv && Number(vv.height);
  const width = vv && Number(vv.width);
  const across = width && Number.isFinite(width)
    ? { left: num(vv.offsetLeft), width }
    : { left: 0, width: '100%' };
  if (!height || !Number.isFinite(height)) return { top: 0, height: '100%', ...across };
  return { top: num(vv.offsetTop), height, ...across };
}

/* ── the page ──────────────────────────────────────────────────────────── */
/* ── the offer a waiting screen shows ────────────────────────────────────────
   The television shape, on the screen that cannot scan. It shows a short code
   and a picture of that code, and then waits — the poll is its parent's, so
   this component draws and says, and decides nothing.

   THE PICTURE AND THE LETTERS ARE BOTH ALWAYS THERE. The picture is for a phone
   held up to the screen; the letters are for a phone across the room, a screen
   reader, and a person reading it out on the telephone. Neither is the fallback
   for the other.

   AND IT SAYS WHAT APPROVING DOES. "This screen will be signed in" is the plain
   version of what a press on the other device grants, and it belongs on this
   side too: somebody who does not know what they are waiting for cannot tell a
   pairing they started from one somebody else is trying to have them start. */
function MdPairOffer({ pair, expired, busy, note, onFresh, onType }) {
  const Qr = window.MdQrCode;
  const url = pair && window.mdGrantUrl ? window.mdGrantUrl(window.location.origin, pair.code) : '';
  return (
    <>
      <p style={mdLandingProse}>
        Show this to a device you are already signed in on. Point its camera at the
        picture, or type the code into Settings there.
      </p>

      {pair && (
        <div style={{ display: 'flex', gap: 18, alignItems: 'center', margin: '18px 0' }}>
          {Qr && url && <Qr text={url} size={132} label="Scan this on a device you are signed in on" />}
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: MD_LANDING_MONO, fontSize: 22, fontWeight: 500, letterSpacing: '0.18em',
              color: MD_SIGNIN.pearl, userSelect: 'text', wordBreak: 'break-all',
            }}>{pair.code}</div>
            <p role="status" style={{ ...mdLandingProse, fontSize: 12, color: '#7B6B93', margin: '10px 0 0' }}>
              {expired
                ? 'This code has expired.'
                : 'Waiting for approval. The moment it is given, this screen is signed in.'}
            </p>
          </div>
        </div>
      )}

      {busy && !pair && (
        <p role="status" style={{ ...mdLandingProse, margin: '18px 0' }}>Making a code…</p>
      )}
      {note && (
        <p role="status" style={{ ...mdLandingProse, color: '#C3A9F0', margin: '18px 0 14px' }}>{note}</p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
        {(expired || (!pair && !busy)) && (
          <button type="button" style={{ ...mdLandingQuietButton, padding: '10px 8px 10px 0' }}
            disabled={busy} onClick={onFresh}>SHOW A NEW CODE</button>
        )}
        <button type="button" style={{ ...mdLandingQuietButton, padding: '10px 8px 10px 0' }}
          onClick={onType}>TYPE A CODE INSTEAD</button>
      </div>
    </>
  );
}

function MdLanding({ onSignedIn, onboarding = false }) {
  const [busy, setBusy] = mdlUseState(null);
  /* True the moment the three questions are owed. Set by enter() after a verb
     succeeds, and by the prop when a page RELOADED into this state — the gate
     answers `onboarding` for a device with a session and no finished profile,
     which is exactly what closing the tab halfway through leaves behind. */
  const [asking, setAsking] = mdlUseState(onboarding);
  /* And it follows the app when the app changes its mind.

     `asking` cannot simply BE the prop: enter() raises it too, on a page whose
     first-frame gate said 'landing', and a prop-driven panel would close in the
     same breath it opened. But when the gate settles the other way — a session
     that expired, or an account deleted from another device, while these
     questions were on screen — the app is saying they are no longer owed, and a
     panel that stayed would be the front door announcing that this device is
     holding an account somebody abandoned halfway through. The one thing the
     film exists to never reveal. So the fall is mirrored; the rise is not.

     Done during the render and not in an effect, on purpose. An effect runs
     after the commit, so the panel would be PAINTED once more before it went —
     one frame of exactly the thing being hidden. Adjusting state here makes
     React re-render before it puts anything on the screen, which is what this
     pattern is for. */
  const wasAsked = mdlUseRef(onboarding);
  if (wasAsked.current !== onboarding) {
    wasAsked.current = onboarding;
    if (!onboarding && asking) setAsking(false);
  }
  const [panel, setPanel] = mdlUseState(null);
  // 'gate' | 'waiting' | 'arrival' | 'fallback' | 'link' | 'verifier'
  /* The first name of somebody whose account exists and whose passkey is not
     here. Held so the fallback can greet them rather than report an error. */
  const [greet, setGreet] = mdlUseState(null);
  const [code, setCode] = mdlUseState('');
  /* ── the two halves of linking a device ───────────────────────────────────
     'code' — a code from elsewhere is typed (or, from OD2-7, scanned) here.
     'pair' — this screen shows a code of its own and waits to be approved.

     Which one leads is the device's answer, not a preference: a desktop with no
     camera cannot scan anything, and a phone almost always can. The answer is a
     PROMISE (`camera` below) rather than a piece of state, because the press
     that needs it can arrive before the browser has spoken — and null, the
     answer from a browser that will not say, leads with the field, which is the
     half that works on both. */
  const [linkMode, setLinkMode] = mdlUseState('code');
  /* The offer this screen is waiting on: the short code it shows, the long
     secret it asks with, and the moment the server stops honouring both. */
  const [pair, setPair] = mdlUseState(null);
  const [pairNote, setPairNote] = mdlUseState(null);
  const [pairExpired, setPairExpired] = mdlUseState(false);
  /* The username being claimed, exactly as it was typed: the slug rule means any
     capitalisation and spacing of the same name resolves to the same registry
     row, so there is nothing to normalise here and nothing to correct at
     somebody. `n` is a string because it comes out of an input. */
  const [who, setWho] = mdlUseState({ first: '', last: '', n: '' });
  const [invited, setInvited] = mdlUseState(null);
  /* Said INSIDE whichever panel is standing. The line under SIGN IN is behind
     the panel — a refusal drawn there while a panel is open is a refusal nobody
     can see. */
  const [panelNote, setPanelNote] = mdlUseState(null);
  const [hover, setHover] = mdlUseState(false);

  const enter = mdlUseCallback(async () => {
    /* Settle through the gate rather than trusting the verb's own answer: the
       gate is what the app asks on every boot, and one authority for "are you
       in" is the point of having it. */
    const gate = await window.mdGateSettle();
    if (gate === 'landing') return false;

    /* THE ROOM IS ONLY KNOWABLE NOW. This page was rendered cold — nothing was
       hydrated, precisely because there was no account to hydrate for — so the
       store is opened here, once the sign-in has said whose it is. Entering
       needs no reload for exactly that reason: there is no other account's
       state on this page to discard. */
    if (window.mdAppBoot) await window.mdAppBoot(window.mdAccountId && window.mdAccountId());

    /* And only now can the profile be read honestly. The settle above ran
       against a store with no room chosen, so its answer is exact for
       `landing` and a guess for the other two — same shape as the pre-render
       gate in md-app.jsx, and for the same reason.

       The pull comes first because this device may be joining an account whose
       profile was finished months ago somewhere else. Without it, linking a
       second device asks the same person the same three questions again. It is
       forced, because the ordinary schedule is "soon" and this is a person
       waiting at a door. A failure is not fatal — the questions get asked, and
       the merge is field-wise, so answering them twice loses nothing. */
    if (typeof window.mdSync === 'function') {
      try { await window.mdSync({ force: true }); } catch (err) { /* ask anyway */ }
    }

    /* THE GATE'S ANSWER STANDS. This line used to read the profile document
       alone, and that is how Marwan's phone came to ask him what his name was:
       he signed in to an account years old, the server said so, the settle above
       had already been told the profile was finished — and then this asked a
       store that had never held anything and got "nothing has been filled in".
       md-gate.ts carries the full story; the fix here is that entering asks the
       same authority the boot does, rather than a second, blinder one.

       And the question is destructive, not merely tiresome: profile fields merge
       by timestamp (app/md-person.ts), so a name typed at that prompt carries a
       later clock than the real one and REPLACES it. */
    /* Both halves have to be there before anybody is held at a question: the
       document that holds the answers, and the authority that reads it. Nothing
       can be owed by an app that cannot say what it holds, and the house style
       here is to ask rather than assume. */
    const answerable = typeof window.mdPersonComplete === 'function'
      && typeof window.mdGateProfiled === 'function';
    if (answerable && !window.mdGateProfiled()) {
      /* One of the questions is answered before it is asked. Two things standing
         here know this person's name: the registry row the server handed over
         with its answer, and the name typed into this very panel three fields
         ago. Written only when there is nothing there — a profile that already
         holds a name is the truth, and a later clock over it is the overwrite
         this whole branch exists to avoid. */
      const known = (window.mdGateName && window.mdGateName())
        || `${who.first} ${who.last}`.replace(/\s+/g, ' ').trim();
      const held = window.mdPersonGet ? window.mdPersonGet('name') : null;
      if (known && !held && window.mdPersonSet) window.mdPersonSet('name', known);

      setPanel(null);
      setAsking(true);
      return true;
    }

    /* The question has been answered, so the panel that asked it goes. Mount
       swaps the landing for the app on the next line, which would take it away
       anyway — but a door left standing over a page that is already inside is
       what a person sees if that swap is a frame late. */
    setPanel(null);
    onSignedIn && onSignedIn();
    return true;
    /* `who` is in here because the name above is read from it: memoised on
       [onSignedIn] alone, this closure would still be holding the three empty
       fields the panel opened with. */
  }, [onSignedIn, who]);

  /* Pressing the only button on the page asks the only question there is. No
     ceremony begins here: a passkey sheet raised over somebody who has never
     had an account is a dialogue they have to dismiss before the page will say
     anything, and the device's own offer below covers everybody it would have
     helped — without a sheet, and without a press. */
  const doSignIn = () => {
    setPanelNote(null);
    setPanel('gate');
  };

  /* The offer, armed while the question stands. Not at page load: the OS
     suggestion bar cannot appear until a field has focus, which is a moment
     later either way, so arming earlier spends a server challenge on every
     visitor who never signs in and starts it going stale before anybody taps.

     Armed only on THIS panel. The fallback below has an explicit TRY PASSKEY
     AGAIN, and a background request armed behind it would be the second live
     ceremony the browser refuses. */
  mdlUseEffect(() => {
    if (panel !== 'gate' || typeof window.mdArmPasskeyAutofill !== 'function') return undefined;
    let held = null;
    let gone = false;
    const stop = () => { if (held && held.ok && typeof held.stop === 'function') held.stop(); };
    Promise.resolve(window.mdArmPasskeyAutofill({ onSignedIn: () => enter() }))
      .then((r) => { held = r; if (gone) stop(); })
      .catch(() => {});
    return () => { gone = true; stop(); };
  }, [panel, enter]);

  /* ── the invitation ───────────────────────────────────────────────────────
     Nobody makes an account here any more. The username somebody else wrote
     down is asked for first, and the answer decides between three completely
     different screens: a welcome, a waiting list, or signing in — because the
     invitation has already been taken up and the person standing here is either
     its owner on a new device or somebody who guessed a name.

     THE THREE ARE NOT COLLAPSIBLE. "Not on the list" and "nobody could be
     asked" look identical from a failed request and mean opposite things: one is
     a decision about this person and the other is a plane. */
  const doCheck = async () => {
    setBusy('check'); setPanelNote(null);
    const r = await window.mdOmanaCheck({ first: who.first, last: who.last, n: Number(who.n) });
    setBusy(null);
    if (!r.ok) {
      setPanelNote(r.reachable === false ? mdLandingSay('unreachable') : mdLandingSay(r.error));
      return;
    }
    if (!r.found) { setPanelNote(null); setPanel('waiting'); return; }
    if (r.claimed) { await doSignInAs(r.first); return; }
    setInvited(r);
    setPanelNote(null);
    setPanel('arrival');
  };

  /* Signing in by the name they typed. The passkey for an account is labelled
     with its username and synced by the ecosystem — iCloud Keychain, Google
     Password Manager — so naming the account is usually enough to bring up the
     right passkey on a device that has never held it. When it is not, the
     device has to be added by hand, which is the door this falls back to. */
  const doSignInAs = async (first) => {
    setBusy('signin'); setPanelNote(null);
    const r = await window.mdSignInPasskey({
      username: { first: who.first, last: who.last, n: Number(who.n) } });
    setBusy(null);
    if (r.ok) { await enter(); return; }
    /* Everything true of somebody standing here is already known: the name is
       real, the account is real, and the passkey for it is not on this device.
       So they are greeted rather than corrected, and offered the three ways
       back in — see the fallback panel. */
    if (first) setGreet(first);
    setPanel('fallback');
  };

  /* The way in for somebody arriving on an invitation, and it is deliberately
     not enter(): the account was minted seconds ago by the claim, so there is
     nothing on any server to pull and nobody to wait for — and the three
     questions have already been answered, two of them by whoever wrote this
     person down. The answers are written AFTER the boot, because before it there
     is no room open to write them into. */
  const enterInvited = async ({ person, dobKey, picture }) => {
    const gate = await window.mdGateSettle();
    if (gate === 'landing') return false;
    if (window.mdAppBoot) await window.mdAppBoot(window.mdAccountId && window.mdAccountId());

    if (window.mdPersonSet) {
      window.mdPersonSet('name', `${person.first} ${person.last}`.trim());
      if (person.gender) window.mdPersonSet('gender', person.gender);
      if (dobKey) window.mdPersonSet('dob', dobKey);
    }
    /* Held until now on purpose: a picture is bytes in the blob store, and the
       blob store needs a session. Best-effort — a picture that will not upload
       is not a reason to stand somebody at a door. */
    if (picture && window.mdPersonPicturePut) {
      try { await window.mdPersonPicturePut(picture); } catch (err) {}
    }
    if (window.mdSyncSoon) window.mdSyncSoon(0);

    /* A refused write is a bug, not a typo, and the honest recovery is to ask
       what is still owed rather than to mark the profile finished over a gap.
       MdOnboarding asks only for the fields with no answer. */
    const done = !window.mdPersonComplete || window.mdPersonComplete();
    if (!done) { setPanel(null); setAsking(true); return true; }
    if (window.mdMarkProfiled) window.mdMarkProfiled();
    onSignedIn && onSignedIn();
    return true;
  };

  /* One redemption for both doors, because there is one kind of code. A
     verifier's rescue code and a signed-in device's link code are the same row
     in the same table with a different prefix (server/omana.js), and
     mdUseDeviceLink redeems it and then mints a passkey for this device under
     the session it just got — so the code is never needed again. */
  const redeem = async (value) => {
    setBusy('link'); setPanelNote(null);
    const r = await window.mdUseDeviceLink(value);
    setBusy(null);
    if (!r.ok) { setPanelNote(mdLandingSay(r.error)); return; }
    await enter();
  };
  const doRedeem = () => redeem(code.trim());

  /* ── the camera at the door ────────────────────────────────────────────────
     The fourth way to get a code across, and the least important of the four:
     the OS camera app reads the same picture, the code can be typed, and a
     screen with no camera can show one instead. So the field is drawn first and
     never moves, the camera is opened only on a press, and every refusal leaves
     the panel exactly as it was.

     THE HANDLE IS A REF, not state. It is hardware with a light on it — the
     cleanups that have to end it (leaving the panel, unmounting the page) must
     see the current one, not the one that was current when they were written. */
  const scanning = mdlUseRef(null);
  const [scanOpen, setScanOpen] = mdlUseState(false);
  const [scanNote, setScanNote] = mdlUseState(null);
  const shutScanner = mdlUseCallback(() => {
    const held = scanning.current;
    scanning.current = null;
    if (held && typeof held.stop === 'function') held.stop();
    setScanOpen(false);
  }, []);

  /* One chokepoint for putting the camera away, rather than a stop() on every
     control that can leave. The camera is allowed open on exactly one surface,
     so anything that moves off it — BACK, crossing to the pair half, a redeemed
     code, a browser Back that unmounts the page — releases it here without
     having been told to. */
  const canScan = (panel === 'link' || panel === 'verifier') && linkMode !== 'pair';
  mdlUseEffect(() => { if (!canScan) shutScanner(); }, [canScan, shutScanner]);
  mdlUseEffect(() => shutScanner, [shutScanner]);

  /* The press only asks for the viewport. Opening the camera is the effect
     below, because the stream has to be attached to an element that is already
     in the document — and an effect is the one place React guarantees that,
     without waiting on a frame callback a hidden tab would never deliver. */
  const openScanner = () => { setScanNote(null); setScanOpen(true); };

  const scanRef = mdlUseRef(null);
  mdlUseEffect(() => {
    if (!scanOpen || scanning.current) return undefined;
    let alive = true;
    (async () => {
      const started = await window.mdScanStart(scanRef.current, (found) => {
        shutScanner();
        setCode(found);
        redeem(found);
      });
      /* Closed while the permission dialogue was still standing. The camera it
         just opened has to be given back, because nothing else is holding it. */
      if (!alive) { if (started && started.ok) started.stop(); return; }
      if (started && started.ok) { scanning.current = started; return; }
      setScanOpen(false);
      /* A refusal is the person's own decision, watched by them, in their own
         browser's dialogue. Explaining it back to them is the app not having been
         listening — the same reason a dismissed passkey sheet says nothing. */
      setScanNote(started && started.error === 'denied' ? null : mdLandingScanSay(started && started.error));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanOpen]);

  /* ── the other direction: this screen asks to be let in ───────────────────
     The shape a television uses. This screen asks the server for an offer — a
     short code it can show and a long secret it keeps — draws the code and a
     picture of it, and then simply waits. Somebody scans the picture with the
     phone they are already signed in on, presses APPROVE once, and the next
     poll here comes back with a session.

     NOBODY EVER TYPES A CODE ON THE DEVICE TRYING TO GET IN, which is the whole
     point: typing is the step this replaces. */
  const startPair = async () => {
    setPair(null); setPairExpired(false); setPairNote(null);
    setBusy('pair');
    const r = await window.mdPairStart();
    setBusy(null);
    if (!r.ok) {
      /* Specific, because the general sentence would be wrong here: nothing the
         person typed is at fault, and there is nothing for them to correct. */
      setPairNote(r.error === 'unreachable' ? mdLandingSay('unreachable')
        : 'A code could not be made just now. Try again in a moment.');
      return;
    }
    const seconds = Number(r.expiresInSeconds) > 0 ? Number(r.expiresInSeconds) : 600;
    setPair({ code: r.code, secret: r.secret, expiresAt: Date.now() + seconds * 1000 });
  };

  /* `mode` names the half explicitly — that is somebody crossing over from the
     other one. Without it the device decides, and deciding means waiting for the
     answer rather than reading whatever has arrived so far. */
  const openLink = async (mode) => {
    setCode(''); setPanelNote(null); setPairNote(null);
    let half = mode;
    if (!half) {
      const seen = camera.current ? await camera.current : null;
      half = seen === false ? 'pair' : 'code';
    }
    setLinkMode(half);
    setPanel('link');
    if (half === 'pair') startPair();
  };

  /* Asked once, the moment the fallback panel goes up — and held as a PROMISE
     rather than as state.

     The press that needs this answer is one button away, so state would be a
     race: a press that landed before the browser replied would open the typed
     half on a desktop with no camera, and the panel is not allowed to change its
     mind once it is drawn. Awaiting the promise removes that race instead of
     narrowing it.

     AND THE PROMISE ALWAYS SETTLES. A browser that has not answered in a second
     and a half is treated as one that will not — null, the third answer, which
     leads with the typed half. A press must never be swallowed waiting for a
     question the person did not ask. */
  const camera = mdlUseRef(null);
  mdlUseEffect(() => {
    if (panel !== 'fallback' || camera.current) return;
    const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!media || typeof media.enumerateDevices !== 'function') {
      camera.current = Promise.resolve(null);
      return;
    }
    camera.current = Promise.race([
      media.enumerateDevices()
        .then((list) => (Array.isArray(list) ? list.some((d) => d && d.kind === 'videoinput') : null))
        .catch(() => null),
      new Promise((settle) => { setTimeout(() => settle(null), MD_CAMERA_ASK_MS); }),
    ]);
  }, [panel]);

  /* The waiting. Polled with the SECRET — the short code on screen can only
     ever approve, and approving needs a session, which is why showing it to a
     room costs nothing. A poll that has outlived the offer stops and says so:
     an app that quietly keeps asking after a dead code looks identical to one
     that has stopped working. */
  mdlUseEffect(() => {
    if (panel !== 'link' || linkMode !== 'pair' || !pair || pairExpired) return undefined;
    let alive = true;
    let timer = 0;
    const ask = async () => {
      const r = await window.mdPairPoll(pair.secret);
      if (!alive) return;
      if (r && r.ok && r.granted) {
        const entered = await enter();
        if (!entered && alive) setPairNote(mdLandingSay('failed'));
        return;
      }
      if (Date.now() >= pair.expiresAt) { setPairExpired(true); return; }
      timer = setTimeout(ask, MD_PAIR_POLL_MS);
    };
    ask();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [panel, linkMode, pair, pairExpired, enter]);

  /* ── a code that arrived in the address ───────────────────────────────────
     The QR on a signed-in screen carries `…/?link=CODE`, so the phone's own
     camera app is the scanner: it sees a link, offers to open it, and this page
     comes up already holding a code. Nothing is asked — the code IS the answer
     to the only question the door has — so it is spent at once, on the panel it
     would have been typed into, and a refusal lands there with the caret ready.

     mdTakeLinkParam strips as it reads (md-qr.ts), which is why there is no
     ordering to get right here: the address cannot still be holding a live
     credential by the time this line has a code in hand. */
  mdlUseEffect(() => {
    const arrived = typeof window.mdTakeLinkParam === 'function' ? window.mdTakeLinkParam() : null;
    if (!arrived) return;
    setCode(arrived);
    setPanel('link');
    redeem(arrived);
  }, []);

  const closePanel = () => {
    setPanel(null); setCode(''); setPanelNote(null); setInvited(null); setGreet(null);
  };

  /* The sign-in panel is a layer on the dismiss arbiter while it stands. The
     three questions are the one surface that holds the key and does nothing with
     it: they cannot be dismissed, and Escape falling through to anything beneath
     them would be worse than Escape doing nothing. */
  window.useMdDismissLayer(() => {
    if (!asking) closePanel();
  }, !!(panel || asking), () => true);
  /* owns everything: the code field exists only while its panel does, so one
     press closes the panel — the search-box rule, and the old behaviour. */

  return (
    <main style={{ position: 'fixed', inset: 0, background: '#000', overflow: 'hidden' }}>
      <h1 style={MD_SR_ONLY}>ManDana — a Muslim&#39;s life in context</h1>

      <MdFilmStage />

      {/* SIGN IN — over the film from the first frame, exactly where it is drawn. */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 52, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, zIndex: 10 }}>
        <button
          type="button"
          onClick={doSignIn}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
          /* NOT AVAILABLE WHILE A PANEL IS STANDING. The overlay covers this
             button, so a mouse cannot reach it — but nothing stopped a Tab from
             the last control in the panel, and the panel's own focus comment has
             said so since it was written. Pressing it there would raise the
             system passkey sheet over a question somebody is halfway through
             answering. Found on a live walkthrough, 2026-08-26. */
          disabled={busy === 'signin' || !!panel || asking}
          style={{
            appearance: 'none', background: 'none', border: 0, cursor: 'pointer',
            /* Drawn at 19; shipped 30% smaller (Marwan, 2026-08-17). */
            fontFamily: MD_LANDING_MONO, fontSize: 13.3, fontWeight: 500,
            letterSpacing: '0.3em', textIndent: '0.3em',
            color: hover ? MD_SIGNIN.hover : MD_SIGNIN.rest,
            opacity: hover ? 1 : MD_SIGNIN.restOpacity,
            padding: '14px 24px',
            transition: 'opacity 0.4s, color 0.4s',
          }}>
          {busy === 'signin' ? 'SIGNING IN' : 'SIGN IN'}
        </button>

      </div>

      {/* The three questions. Holds the layer and answers nothing to Escape —
          the way out backwards is an account that is signed in and has answered
          nothing, which this app has no surface for. */}
      {asking && (
        <MdOnboarding onDone={() => { setAsking(false); onSignedIn && onSignedIn(); }} />
      )}

      {/* THE QUESTION AT THE DOOR. Three fields and the namespace they live on,
          because a username here is not chosen: it was minted when somebody
          wrote this person's name down, and typing it back is how they prove it.
          Any capitalisation and any spacing reach the same row. */}
      {panel === 'gate' && !asking && (
        <MdLandingPanel title="What is your username?">
          {/* Two rows, not three columns. Three boxes across a 375px phone leave
              seven characters for a first name, and the name this phase was
              specified with is eleven. The first name takes the row; the last
              name shares the second one with a two-digit number, which is all
              an Auto ID ever is. Fluid at every width — no breakpoint to be
              wrong about, and nothing to measure. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.62fr', gap: 10, margin: '4px 0 6px' }}>
            {/* `username webauthn` on the first field, and it is load-bearing:
                the operating system hangs its passkey suggestion on a focused
                input that claims to take a username, and the `webauthn` half is
                what makes the offer a passkey rather than a saved string.
                Dropped, everything still works and the one-tap sign-in
                silently stops existing. */}
            {[['first', 'First Name', 'username webauthn'], ['last', 'Last Name', 'family-name'], ['n', 'Auto ID', 'off']]
              .map(([part, label, auto]) => (
                <MdLandingField key={part} id={`md-omana-${part}`} label={label} value={who[part]}
                  inputMode={part === 'n' ? 'numeric' : undefined}
                  autoComplete={auto}
                  wrapStyle={part === 'first' ? { gridColumn: '1 / -1' } : undefined}
                  style={part === 'n' ? { textAlign: 'center' } : undefined}
                  onChange={(e) => setWho({ ...who,
                    [part]: part === 'n' ? e.target.value.replace(/\D/g, '') : e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && mdOmanaAsked(who) && !busy) doCheck(); }} />
              ))}
          </div>
          {/* Rendered, not implied: it is part of the address, and a person who
              cannot see it cannot know what they are being given. */}
          <p style={{ ...mdLandingProse, fontSize: 12, color: '#7B6B93', margin: '0 0 18px' }}>
            {mdOmanaAsked(who)
              ? `${who.first.trim().toLowerCase().replace(/\s+/g, '')}.${who.last.trim().toLowerCase().replace(/\s+/g, '')}.${who.n}@omana.network`
              : 'first.last.auto-ID@omana.network'}
          </p>
          {panelNote && <p role="status" style={{ ...mdLandingProse, color: '#C3A9F0', marginBottom: 14 }}>{panelNote}</p>}
          {/* One grammar in every panel: the way forward sits at the right end,
              the way back — CANCEL, BACK — at the left. A thumb learns where
              CONTINUE is once, and then it is always there. */}
          <div style={{ display: 'flex', gap: '10px 18px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" style={mdLandingQuietButton} onClick={closePanel}>CANCEL</button>
            <button type="button" style={{ ...mdLandingQuietButton, marginLeft: 'auto' }}
              disabled={!mdOmanaAsked(who) || !!busy} onClick={doCheck}>
              {busy ? 'CHECKING' : 'CONTINUE'}
            </button>
          </div>
        </MdLandingPanel>
      )}

      {/* Not on the list. There is nothing to try again and nothing collected
          that could reach this person later, so there is one door out and it goes
          back to the film. */}
      {panel === 'waiting' && !asking && (
        <MdLandingPanel title="You are on the list">
          <p style={mdLandingProse}>{MD_WAITING_LIST}</p>
          <div style={{ marginTop: 18 }}>
            <button type="button" style={mdLandingQuietButton} onClick={closePanel}>CLOSE</button>
          </div>
        </MdLandingPanel>
      )}

      {panel === 'arrival' && invited && !asking && (
        <MdInvitedArrival person={invited} busy={busy} setBusy={setBusy}
          onEnter={({ dobKey, picture }) => enterInvited({ person: invited, dobKey, picture })} />
      )}

      {/* THE FALLBACK. An account that exists, on a device with no passkey for
          it — the person dismissed the sheet, or the ecosystem never carried the
          passkey across. Three ways back in, and they are siblings of equal
          rank rather than a first choice and two consolations: which one is
          right depends entirely on what this person has to hand. CANCEL sits at
          the left on its own, because the right/left grammar governs pairs that
          go forward and back, and none of these three is "forward". */}
      {panel === 'fallback' && !asking && (
        <MdLandingPanel title={greet ? `Hey ${greet}` : 'This device needs a passkey'}>
          <p style={{ ...mdLandingProse, margin: '4px 0 18px' }}>
            Your account is here. Its passkey is not on this device.
          </p>
          {panelNote && <p role="status" style={{ ...mdLandingProse, color: '#C3A9F0', marginBottom: 14 }}>{panelNote}</p>}
          <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
            {[
              ['again', 'TRY PASSKEY AGAIN', 'If the sheet was dismissed, or the wrong passkey was offered.'],
              ['link', 'LINK THIS DEVICE', 'Use a code from a device you are already signed in on.'],
              ['verifier', 'ASK YOUR VERIFIER', 'One of your trusted verifiers can generate a sign-in code for you.'],
            ].map(([key, label, why]) => (
              <button
                key={key}
                type="button"
                disabled={!!busy}
                onClick={() => {
                  setPanelNote(null);
                  if (key === 'again') { doSignInAs(greet); return; }
                  /* A screen the browser says has no camera cannot scan the code
                     it is being told to scan, so it opens on the half that asks
                     to be approved instead. Everything else — a camera, or a
                     browser that will not say — opens on the field, which works
                     either way. */
                  if (key === 'link') { openLink(); return; }
                  setCode('');
                  setPanel(key);
                }}
                style={{
                  appearance: 'none', textAlign: 'left', cursor: busy ? 'default' : 'pointer',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(195,169,240,0.28)',
                  borderRadius: 10, padding: '14px 16px', display: 'block', width: '100%',
                }}>
                <span style={{ ...mdLandingQuietButton, padding: 0, display: 'block' }}>
                  {busy === 'signin' && key === 'again' ? 'ASKING' : label}
                </span>
                <span style={{ ...mdLandingProse, display: 'block', fontSize: 12, color: '#7B6B93', margin: '6px 0 0' }}>
                  {why}
                </span>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex' }}>
            <button type="button" style={mdLandingQuietButton} onClick={closePanel}>CANCEL</button>
          </div>
        </MdLandingPanel>
      )}

      {/* Two ways into one account, and which of them leads was decided by the
          device before this panel opened. The other is always one press away,
          because the decision was made from what the browser would say about a
          camera — which is a guess about a person's situation, not a fact about
          it. The verifier's door has no second half: a verifier's code is
          always typed or scanned, never approved. */}
      {(panel === 'link' || panel === 'verifier') && !asking && (
        <MdLandingPanel title={panel === 'link' ? 'Link this device' : 'Ask your verifier'}>
          {panel === 'link' && linkMode === 'pair' ? (
            <MdPairOffer
              pair={pair} expired={pairExpired} busy={busy === 'pair'} note={pairNote}
              onFresh={startPair} onType={() => { setPairNote(null); setLinkMode('code'); }} />
          ) : (
            <>
              <p style={mdLandingProse}>
                {panel === 'link'
                  ? 'On a device already signed in, open Settings and ask for a code. Enter it here.'
                  : 'Ask one of your trusted verifiers to generate a sign-in code for you, and enter it here.'}
              </p>
              {/* The viewport sits ABOVE the field and never in place of it. It
                  is only in the tree while the camera is open, because an empty
                  black rectangle is a camera that appears to have failed. */}
              {scanOpen && (
                <video
                  id="md-landing-scan" ref={scanRef}
                  aria-label="Camera — point it at the code on your other device"
                  playsInline muted
                  style={{
                    width: '100%', aspectRatio: '4 / 3', margin: '18px 0 0', display: 'block',
                    background: '#000', objectFit: 'cover', borderRadius: 10,
                    border: '1px solid rgba(195,169,240,0.35)',
                  }} />
              )}
              <label style={MD_SR_ONLY} htmlFor="md-landing-code">Code</label>
              {/* Enter submits, exactly as it does on the name gate. A phone
                  keyboard turns a one-time-code field's return key blue and
                  labels it go, so a field that ignores it reads as the code
                  having been refused. */}
              <input
                id="md-landing-code"
                value={code}
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && code.trim() && !busy) doRedeem(); }}
                style={{
                  width: '100%', margin: '18px 0', padding: '12px 14px', background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(195,169,240,0.35)', borderRadius: 8, color: MD_SIGNIN.pearl,
                  fontFamily: MD_LANDING_MONO, fontSize: 16, letterSpacing: '0.14em',
                }} />
              {panelNote && <p role="status" style={{ ...mdLandingProse, color: '#C3A9F0', marginBottom: 14 }}>{panelNote}</p>}
              {scanNote && <p role="status" style={{ ...mdLandingProse, color: '#C3A9F0', marginBottom: 14 }}>{scanNote}</p>}
              {/* Offered only where it can work. Safari ships no decoder and
                  this app ships none either (app/md-scan.ts) — and on Safari the
                  OS camera app reads the very same picture in two taps. A button
                  that can only apologise is worse than no button. */}
              {!scanOpen && window.mdScanSupported && window.mdScanSupported() && (
                <button type="button" style={{ ...mdLandingQuietButton, padding: '10px 0', marginBottom: 4, display: 'block' }}
                  onClick={openScanner}>SCAN THE CODE</button>
              )}
              {scanOpen && (
                <button type="button" style={{ ...mdLandingQuietButton, padding: '10px 0', marginBottom: 4, display: 'block' }}
                  onClick={shutScanner}>STOP THE CAMERA</button>
              )}
              {panel === 'link' && (
                <button type="button" style={{ ...mdLandingQuietButton, padding: '10px 0', marginBottom: 4, display: 'block' }}
                  onClick={() => openLink('pair')}>SHOW A CODE INSTEAD</button>
              )}
            </>
          )}
          <div style={{ display: 'flex', gap: '10px 18px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* BACK, not CANCEL: the way out of here is the greeting it came
                from, where the other two doors are still standing. */}
            <button type="button" style={mdLandingQuietButton}
              onClick={() => { setCode(''); setPanelNote(null); setPair(null); setPanel('fallback'); }}>BACK</button>
            {!(panel === 'link' && linkMode === 'pair') && (
              <button type="button" style={{ ...mdLandingQuietButton, marginLeft: 'auto' }}
                disabled={!code.trim() || !!busy} onClick={doRedeem}>
                {busy ? 'CHECKING' : 'CONTINUE'}
              </button>
            )}
          </div>
        </MdLandingPanel>
      )}
    </main>
  );
}

/* ── the three questions ─────────────────────────────────────────────────────
   An account is required to use ManDana, and before the first entry the app
   asks for a name, a date of birth and a gender. Not to profile anybody: a
   life in context is measured against a date of birth, and a name is what the
   app calls you instead of calling you nothing. A picture is offered and is
   plainly optional.

   ON THE LANDING SIDE, OVER THE FILM. The front door looks identical to every
   visitor, always — it must never reveal how many accounts a device holds, or
   that this one is halfway through being set up — so this is a panel on the
   landing page rather than a fourth screen with a life of its own.

   IT CANNOT BE DISMISSED, and that is not an oversight to be softened later.
   The way out backwards is an account that exists, is signed in, and has
   answered nothing: a state this app has no surface for. So Escape does
   nothing, there is no CANCEL, and the only direction is forward or back
   through the questions. It wears the same armour as the recovery code, for
   the same reason.

   NOTHING IS WRITTEN UNTIL THE END. Every answer is held in this component and
   committed once, on the last step. Somebody who closes the tab on the second
   question is asked three questions next time rather than one, and has left no
   half-profile for another device to merge — which matters more than it
   sounds, because the merge is field-wise and a stray half-answer would win. */

/* A date of birth is not a date this app picks from a calendar. Every other
   date here is near today and a month grid is the right instrument for it;
   this one is thirty or fifty years back, where a grid means paging four
   hundred times. Three numbers, in the order they are said aloud, and the
   document's own validator decides whether they are a day. */
function MdDobField({ value, onChange }) {
  const set = (part) => (event) => onChange({ ...value, [part]: event.target.value.replace(/\D/g, '') });
  /* The panel takes focus when it OPENS, and it stays open across the steps of
     a wizard — so a field that arrives on step two was reached only by tabbing
     to it. This is the field arriving; it takes the caret itself. */
  const day = mdlUseRef(null);
  mdlUseEffect(() => { if (day.current) day.current.focus(); }, []);
  /* The same field as the door's, for the same two reasons: a ring drawn inside
     the box cannot paint over DAY, and a named caret is the only thing that says
     a box on a black panel is listening. */
  const box = { textAlign: 'center', fontSize: 16, letterSpacing: '0.1em' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 10, margin: '18px 0' }}>
      {[['day', 'DAY', 2], ['month', 'MONTH', 2], ['year', 'YEAR', 4]].map(([part, label, len]) => (
        <MdLandingField key={part} id={`md-dob-${part}`} label={label} inputMode="numeric"
          maxLength={len} innerRef={part === 'day' ? day : null}
          value={value[part]} onChange={set(part)} style={box} />
      ))}
    </div>
  );
}

/* Gregorian, and the field says so. It is the one date in this app asked for
   differently from every other — days, prayers and fasts are Hijri here — and
   the reason is simply that a birthday is not kept on a lunar clock by the
   people whose birthday it is. */
const mdDobKey = (v) => {
  const y = v.year, m = v.month, d = v.day;
  if (!y || !m || !d) return '';
  return `${y.padStart(4, '0')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

/* ── the arrival ───────────────────────────────────────────────────────────
   Four screens, in the order Marwan drew them, and the order is the argument:
   you are welcomed before you are asked anything, the two questions the
   registry could not answer are asked one at a time, and the passkey — the
   thing that actually makes the account — is last and on its own.

   NOTHING IS WRITTEN UNTIL THE PASSKEY LANDS. The date and the picture are held
   right here in state, because until the claim is verified there is no session,
   no room, and nowhere to put them. A person who closes the tab on the third
   screen has left nothing behind; the username is still theirs.

   AND THE NAME AND THE GENDER ARE NEVER ASKED. Somebody wrote them down. Asking
   the invited person to confirm what their verifier already stated would turn a
   welcome into a form. */
function MdInvitedArrival({ person, onEnter, busy, setBusy }) {
  const [step, setStep] = mdlUseState(0);
  const [dob, setDob] = mdlUseState({ day: '', month: '', year: '' });
  const [picture, setPicture] = mdlUseState(null);
  const [note, setNote] = mdlUseState(null);

  const dobKey = mdDobKey(dob);
  const dobOk = !!dobKey && (!window.mdPersonDobOk || window.mdPersonDobOk(dobKey));

  const STEPS = [
    { title: `Welcome ${person.first}`, ready: () => true },
    { title: 'When did Allah send you to Earth?', ready: () => dobOk },
    { title: `Let's add a nice picture of you`, ready: () => true },
    { title: 'One last thing', ready: () => true },
  ];
  const last = step === STEPS.length - 1;

  /* The account is made here, and only here. A refusal leaves every answer in
     the form and the button where it was: the username is not spent, and the
     honest next move is to press it again. */
  const create = async () => {
    setBusy('passkey'); setNote(null);
    const r = await window.mdRegisterPasskey({
      claim: { first: person.first, last: person.last, n: person.n } });
    if (!r.ok) { setBusy(null); setNote(mdLandingSay(r.error)); return; }
    const entered = await onEnter({ dobKey, picture });
    setBusy(null);
    if (!entered) setNote(mdLandingSay('failed'));
  };

  const forward = () => { if (last) { create(); return; } setNote(null); setStep(step + 1); };

  return (
    <MdLandingPanel title={STEPS[step].title}>
      {step === 0 && (
        <>
          {/* The verifier's name, when the registry could give one. A person who
              was vouched for should know by whom: it is the whole of what this
              door is. Omitted rather than guessed at when it is null. */}
          {person.verifier
            ? <p style={mdLandingProse}>{person.verifier} is your trusted verifier.</p>
            : <p style={mdLandingProse}>Somebody already on ManDana wrote your name down.</p>}
          <p style={{ ...mdLandingProse, margin: '18px 0 0', color: MD_SIGNIN.pearl,
            fontSize: 13.5, letterSpacing: '0.06em', wordBreak: 'break-all' }}>
            {person.username}
          </p>
        </>
      )}

      {step === 1 && <MdDobField value={dob} onChange={setDob} />}

      {step === 2 && (
        <>
          <label htmlFor="md-arrive-pic" style={{ ...mdLandingQuietButton, display: 'inline-block', margin: '4px 0 18px', cursor: 'pointer' }}>
            {picture ? 'PICTURE CHOSEN' : 'CHOOSE A PICTURE'}
          </label>
          {/* Held, not uploaded: there is no session yet. It goes up inside
              enterInvited, once there is a room to put it in. */}
          <input id="md-arrive-pic" type="file" accept="image/*"
            aria-label="A picture of you"
            onChange={(e) => setPicture((e.target.files && e.target.files[0]) || null)}
            style={{ ...MD_SR_ONLY, width: 1, height: 1 }} />
        </>
      )}


      {note && (
        <p role="status" style={{ ...mdLandingProse, color: '#C3A9F0', margin: '18px 0 14px' }}>{note}</p>
      )}

      <div style={{ display: 'flex', gap: '10px 18px', flexWrap: 'wrap', alignItems: 'center', marginTop: 18 }}>
        {/* Backwards through the questions, never out of them — and never back
            past the welcome, because there is nothing behind it but the door.
            Left end, where every way back in this app lives. */}
        {step > 0 && (
          <button type="button" style={mdLandingQuietButton} disabled={!!busy}
            onClick={() => { setNote(null); setStep(step - 1); }}>BACK</button>
        )}
        <button type="button" style={{ ...mdLandingQuietButton, marginLeft: 'auto' }}
          disabled={!!busy || !STEPS[step].ready()} onClick={forward}>
          {/* One button, and it says what pressing it will actually do. With no
              picture chosen, CONTINUE and a separate NOT NOW were two doors into
              the same room — so the forward button simply becomes NOT NOW, and
              CHOOSE A PICTURE is right above it. */}
          {busy ? 'CREATING' : last ? 'CREATE DEVICE PASSKEY'
            : (step === 2 && !picture) ? 'NOT NOW' : 'CONTINUE'}
        </button>
      </div>
    </MdLandingPanel>
  );
}

function MdOnboarding({ onDone }) {
  const [step, setStep] = mdlUseState(0);
  const [name, setName] = mdlUseState('');
  const [dob, setDob] = mdlUseState({ day: '', month: '', year: '' });
  const [gender, setGender] = mdlUseState(null);
  const [busy, setBusy] = mdlUseState(false);
  const [note, setNote] = mdlUseState(null);

  const genders = window.MD_PERSON_GENDERS || ['female', 'male'];
  const dobKey = mdDobKey(dob);
  const dobOk = !!dobKey && (!window.mdPersonDobOk || window.mdPersonDobOk(dobKey));

  /* WHAT IS STILL OWED, DECIDED ONCE AT MOUNT. Some of these answers arrive
     from somewhere else: an invitation carries the name and the gender, and a
     linked device pulls a profile that was finished months ago somewhere else.
     Asking again for what is already known reads as the app not having listened.

     Read once and held rather than recomputed each render, because commit
     writes these fields — a live list would drop the step being answered out
     from under the person answering it. */
  const owed = mdlUseRef(null);
  if (owed.current === null) {
    const has = (f) => !!(window.mdPersonGet && window.mdPersonGet(f));
    /* AND THE NAME IS ANSWERED BEFORE THE LIST IS MADE. Whoever wrote this
       person down named them, so the server hands the name over with its answer
       about the profile (app/md-gate.ts) — and a question this app already knows
       the answer to is not a question. Written only into a gap: a name in the
       document is the truth and its clock is the real one, and these fields
       merge by clock. Entering does the same thing one door earlier, where the
       name typed at the gate is also standing; this covers every other way in. */
    const handed = window.mdGateName && window.mdGateName();
    if (handed && !has('name') && window.mdPersonSet) window.mdPersonSet('name', handed);
    owed.current = (window.MD_PERSON_REQUIRED || ['name', 'dob', 'gender']).filter((f) => !has(f));
  }
  const asked = owed.current;

  const ALL = {
    name: { title: 'What is your name?', ready: () => !!name.trim() },
    dob: { title: 'When were you born?', ready: () => dobOk },
    gender: { title: 'Gender', ready: () => !!gender },
  };
  /* The picture is always last and always offered: it is the one thing here
     nobody is required to answer, so it can be asked of everybody. */
  const STEPS = [...asked.map((f) => ({ key: f, ...ALL[f] })),
    { key: 'picture', title: 'A picture, if you like', ready: () => true }];
  const on = (key) => !!STEPS[step] && STEPS[step].key === key;

  /* The one write. Three fields and the device's own note, in that order, and
     only then the door. mdMarkProfiled is what lets the NEXT boot answer on
     the first frame without waiting for storage to hydrate. */
  const commit = async () => {
    setBusy(true);
    /* Only what was asked. A field written back from an empty form would be
       this panel erasing an answer it deliberately never showed. */
    const write = { name: () => window.mdPersonSet('name', name.trim()),
      dob: () => window.mdPersonSet('dob', dobKey),
      gender: () => window.mdPersonSet('gender', gender) };
    const wrote = asked.every((f) => write[f]());
    setBusy(false);
    if (!wrote) {
      /* A refusal here is a bug, not a typo — the form gated every field on the
         same validator the document uses. Said rather than swallowed, because
         the alternative is a CONTINUE that does nothing and no explanation. */
      setNote('Something in that could not be saved. Please check the answers.');
      return;
    }
    if (window.mdMarkProfiled) window.mdMarkProfiled();
    /* The other devices should not wait for a schedule to learn your name. */
    if (window.mdSyncSoon) window.mdSyncSoon(0);
    onDone();
  };

  const forward = async () => {
    if (step < STEPS.length - 1) { setNote(null); setStep(step + 1); return; }
    await commit();
  };

  const pickPicture = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file || !window.mdPersonPicturePut) return;
    setBusy(true); setNote(null);
    const put = await window.mdPersonPicturePut(file);
    setBusy(false);
    /* A picture that would not upload is not a reason to stand at a door. Said
       plainly, and SKIP is still right there. */
    if (!put.ok) setNote('That picture could not be saved. You can add one later.');
  };

  return (
    <MdLandingPanel title={STEPS[step].title}>
      {on('name') && (
        <>
          <label style={MD_SR_ONLY} htmlFor="md-onb-name">Your name</label>
          <input id="md-onb-name" value={name} autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            style={{
              width: '100%', margin: '4px 0 18px', padding: '12px 14px', background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(195,169,240,0.35)', borderRadius: 8, color: MD_SIGNIN.pearl,
              fontFamily: MD_LANDING_MONO, fontSize: 16, letterSpacing: '0.06em',
            }} />
        </>
      )}

      {on('dob') && <MdDobField value={dob} onChange={setDob} />}

      {on('gender') && (
        <>
          <div role="radiogroup" aria-label="Gender" style={{ display: 'flex', gap: 10, margin: '4px 0 18px' }}>
            {genders.map((word) => (
              <button key={word} type="button" role="radio" aria-checked={gender === word ? 'true' : 'false'}
                onClick={() => setGender(word)}
                style={{
                  ...mdLandingQuietButton, flex: 1, padding: '12px 8px', borderRadius: 8,
                  border: `1px solid ${gender === word ? 'rgba(195,169,240,0.75)' : 'rgba(195,169,240,0.28)'}`,
                  background: gender === word ? 'rgba(195,169,240,0.14)' : 'transparent',
                  color: gender === word ? MD_SIGNIN.pearl : MD_SIGNIN.hover,
                }}>
                {word.toUpperCase()}
              </button>
            ))}
          </div>
        </>
      )}

      {on('picture') && (
        <>
          <label htmlFor="md-onb-pic" style={{ ...mdLandingQuietButton, display: 'inline-block', margin: '4px 0 18px', cursor: 'pointer' }}>
            CHOOSE A PICTURE
          </label>
          <input id="md-onb-pic" type="file" accept="image/*" onChange={pickPicture}
            style={{ ...MD_SR_ONLY, width: 1, height: 1 }} />
        </>
      )}

      {note && (
        <p role="status" style={{ ...mdLandingProse, color: '#C3A9F0', marginBottom: 14 }}>{note}</p>
      )}

      <div style={{ display: 'flex', gap: '10px 18px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Backwards through the questions, never out of them. Answers already
            given stay in the form: this is a wizard, not a series of
            interrogations. Left end, with every other way back. */}
        {step > 0 && (
          <button type="button" style={mdLandingQuietButton} disabled={busy}
            onClick={() => { setNote(null); setStep(step - 1); }}>BACK</button>
        )}
        {/* SKIP travels with the primary rather than with BACK: it is a lesser
            way FORWARD — it enters the app — and putting it beside the way out
            would say the opposite of what it does. */}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'center' }}>
          {step === STEPS.length - 1 && (
            <button type="button" style={mdLandingQuietButton} disabled={busy} onClick={commit}>SKIP</button>
          )}
          <button type="button" style={mdLandingQuietButton} disabled={busy || !STEPS[step].ready()}
            onClick={forward}>
            {busy ? 'SAVING' : (step === STEPS.length - 1 ? 'ENTER' : 'CONTINUE')}
          </button>
        </span>
      </div>
    </MdLandingPanel>
  );
}

const mdLandingQuietButton = {
  appearance: 'none', background: 'none', border: 0, cursor: 'pointer',
  fontFamily: MD_LANDING_MONO, fontSize: 12, fontWeight: 500,
  letterSpacing: '0.22em', textIndent: '0.22em',
  color: MD_SIGNIN.hover, padding: '10px 8px',
};

const mdLandingProse = {
  margin: 0, fontFamily: MD_LANDING_MONO, fontSize: 12.5, lineHeight: 1.75,
  letterSpacing: '0.04em', color: '#B9AFCC',
};

const mdLandingField = {
  width: '100%', padding: '12px 10px', background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(195,169,240,0.35)', borderRadius: 8, color: MD_SIGNIN.pearl,
  /* 16 is a floor, not a taste. Mobile Safari zooms the whole page when a
     focused input's text is smaller than this, and the zoom narrows and pans
     the visual viewport while the layout viewport stays — so the card came out
     shifted right and clipped at the screen edge the moment the keyboard came
     up. There is no CSS that declines the zoom; the only way out is 16. */
  fontFamily: MD_LANDING_MONO, fontSize: 16, letterSpacing: '0.06em',
};

/* `display: block` is not decoration — it is the fix.
   A <label> is inline by default, and a bottom margin on an inline box does
   nothing in the block direction. So `0 0 6px` was silently ignored and the
   caption sat 1.5px above the field, which is why the browser's focus ring —
   drawn outside the border — painted straight through the words "First name" on
   a phone. Blocked, the margin applies and the two never touch. */
const mdLandingCaption = {
  display: 'block', margin: '0 0 6px', fontFamily: MD_LANDING_MONO, fontSize: 10,
  letterSpacing: '0.22em', color: '#7B6B93',
};

/* One field at the door, and the reason it is a component rather than a style
   object: FOCUS.

   The browser's own focus ring is drawn OUTSIDE the border, and the caption sits
   six pixels above the box — so on a phone, focusing First name painted a purple
   rounded rectangle straight through the words "First name". A ring drawn INSIDE
   the box cannot reach the label whatever the gap is, which is why the outline
   goes and an inset one replaces it. It has to be a real, obvious ring: a
   keyboard user needs to see where they are, and this is the only indication.

   And the caret is named. It was invisible on a black panel with the ring gone —
   a field with the keyboard up, a border and no caret reads as one that is not
   listening. */
function MdLandingField({ id, label, value, onChange, style, wrapStyle, innerRef, ...rest }) {
  const [lit, setLit] = mdlUseState(false);
  return (
    <span style={wrapStyle}>
      <label htmlFor={id} style={mdLandingCaption} className="md-dob-cap">{label}</label>
      <input id={id} aria-label={label} value={value} onChange={onChange} ref={innerRef}
        onFocus={() => setLit(true)} onBlur={() => setLit(false)}
        style={{
          ...mdLandingField,
          ...style,
          caretColor: MD_SIGNIN.hover,
          outline: 'none',
          borderColor: lit ? 'rgba(195,169,240,0.85)' : (style && style.borderColor) || 'rgba(195,169,240,0.35)',
          boxShadow: lit ? 'inset 0 0 0 1.5px rgba(195,169,240,0.55)' : 'none',
        }}
        {...rest} />
    </span>
  );
}

/* Both names and the Auto ID, and all three are asked for.

   THE AUTO ID IS NOT GUESSED. It was decided by the register the moment somebody
   wrote this person down, and it is printed in the invitation they were sent — so
   the box starts empty and CONTINUE waits for it. A 1 sitting there is right for
   most people and quietly wrong for the second Alex Barreto, who would press
   CONTINUE on somebody else's invitation without ever being asked. */
const mdOmanaAsked = (who) => !!who.first.trim() && !!who.last.trim() && !!who.n;

/* A quiet card over the film. Deliberately plain: the film is the design and a
   second visual idea competing with it would be one too many. */
function MdLandingPanel({ title, children }) {
  const box = mdlUseRef(null);

  /* The layer follows the visual viewport, so a raised keyboard takes room away
     from the panel instead of covering it. Both events matter: `resize` is the
     keyboard opening, `scroll` is the page being pushed up under it. */
  const [room, setRoom] = mdlUseState(() => mdLandingKeyboardBox(window.visualViewport));
  mdlUseEffect(() => {
    const vv = window.visualViewport;
    if (!vv || typeof vv.addEventListener !== 'function') return undefined;
    const onChange = () => setRoom(mdLandingKeyboardBox(vv));
    vv.addEventListener('resize', onChange);
    vv.addEventListener('scroll', onChange);
    return () => {
      vv.removeEventListener('resize', onChange);
      vv.removeEventListener('scroll', onChange);
    };
  }, []);

  /* aria-modal says "nothing outside this matters" and then focus is still on
     the body, one Tab away from the SIGN IN button sitting behind the panel.
     So the panel takes focus when it opens — the field if it has one, itself
     otherwise — and hands it back on the way out.
     Handing it back needs the fallback: a panel is often opened from another
     panel, so by the time this cleanup runs the button that was clicked is
     usually detached, and focusing a detached node lands on <body>. SIGN IN is
     the one control that is always on the page. */
  mdlUseEffect(() => {
    /* `document.activeElement` is <body> when nothing is focused, and <body> is
       always connected — so it has to be excluded here or the restore below
       "succeeds" by focusing the body, which is where it was trying not to end. */
    const active = document.activeElement;
    const opener = active && active !== document.body ? active : null;
    const el = box.current;
    const first = el && (el.querySelector('input, textarea, button') || el);
    if (first) first.focus();
    /* And if it opens holding something, that something is SELECTED. A panel is
       often reopened to correct one letter of what is already in it — a name
       that came back off the waiting list, most often — so the value is kept.
       Kept with the caret parked after it, though, the next keystroke lands on
       the END of the refused name: on staging that turned a second attempt into
       a waiting-list row for "nobodymarwan.heregalaly.11". Selected, one
       keystroke replaces it and one arrow key keeps it. */
    try { if (first && first.value) first.select(); } catch (_) {}
    return () => {
      /* One tick late, on purpose. The cleanup runs BEFORE React detaches the
         panel, so anything focused here is un-focused by the commit that
         follows and focus lands on <body> anyway. */
      queueMicrotask(() => {
        const held = document.activeElement;
        if (held && held !== document.body && held.isConnected) return;
        const back = opener && opener.isConnected ? opener : document.querySelector('main button');
        if (back && typeof back.focus === 'function') back.focus();
      });
    };
  }, []);

  return (
    <div style={{
      position: 'absolute', left: room.left, width: room.width, top: room.top, height: room.height,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      /* Centred while it fits, scrolling when it does not. A panel taller than
         the room the keyboard left must put its buttons somewhere reachable, and
         `center` alone would crop them off both ends. */
      overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      /* 20 sideways is the visible margin; 16 up and down is room bought back
         for the tightest case there is — a small phone with the keyboard up,
         where four pixels decide whether the card fits or has to be scrolled. */
      zIndex: 20, padding: '16px 20px',
    }}>
      <div role="dialog" aria-modal="true" aria-label={title} ref={box} tabIndex={-1}
        style={{
          width: 'min(420px, 100%)', margin: 'auto', flex: '0 0 auto',
          padding: '28px 26px 22px', background: 'rgba(8,6,14,0.92)',
          border: '1px solid rgba(195,169,240,0.28)', borderRadius: 18,
          boxShadow: '0 30px 90px rgba(0,0,0,0.7)', backdropFilter: 'blur(18px)',
        }}>
        <p style={{ margin: '0 0 14px', fontFamily: MD_LANDING_MONO, fontSize: 11, letterSpacing: '0.26em', textIndent: '0.26em', textTransform: 'uppercase', color: MD_SIGNIN.hover }}>
          {title}
        </p>
        {children}
      </div>
    </div>
  );
}

Object.assign(window, { MdLanding, MdOnboarding, mdLandingBox, mdLandingKeyboardBox });
